/**
 * Scenario merging utility for delta/patch scenarios.
 *
 * Merges a delta scenario on top of a base scenario using these rules:
 *
 * - **agents**: if an agent.id exists in delta, swap that agent in; all others kept from base.
 *   NOTE: delta agents whose id does not exist in base are silently dropped (not added).
 *   To add new agents in a delta, they must first be added to the base scenario.
 *
 * - **analysis**: if present in delta, replaces the entire analysis object.
 *
 * - **meta, clockAnnotations, viewportPins, speedControls, autoPan**:
 *   if present in delta, replaces the corresponding field wholesale.
 *
 * Optional `metaOverrides` can be passed to override specific meta fields after the merge
 * (e.g. to set a custom name for the merged scenario).
 */

export interface ScenarioDelta {
    meta?: Record<string, any>
    agents?: Array<{ id: string; [key: string]: any }>
    analysis?: Record<string, any>
    clockAnnotations?: any[]
    speedControls?: any[]
    viewportPins?: any[]
    autoPan?: boolean
}

export function mergeScenarios(
    base: Record<string, any>,
    delta: ScenarioDelta,
    metaOverrides?: Record<string, any>
): Record<string, any> {
    // Deep clone base so we never mutate the imported JSON
    const merged: Record<string, any> = JSON.parse(JSON.stringify(base))

    // --- Meta ---
    if (delta.meta) {
        merged.meta = { ...delta.meta }
    }
    if (metaOverrides) {
        merged.meta = { ...merged.meta, ...metaOverrides }
    }

    // --- Agents: swap by id ---
    if (delta.agents && delta.agents.length > 0) {
        const deltaById = new Map(delta.agents.map((a) => [a.id, a]))
        merged.agents = (merged.agents as any[]).map((agent) =>
            deltaById.has(agent.id) ? deltaById.get(agent.id) : agent
        )
    }

    // --- Analysis ---
    if (delta.analysis) {
        merged.analysis = { ...delta.analysis }
    }

    // --- Top-level wholesale replacements ---
    if (delta.clockAnnotations) merged.clockAnnotations = delta.clockAnnotations
    if (delta.speedControls) merged.speedControls = delta.speedControls
    if (delta.viewportPins) merged.viewportPins = delta.viewportPins
    if (delta.autoPan !== undefined) merged.autoPan = delta.autoPan

    return merged
}
